export type BotType = "booking_bot" | "admissions_bot" | "sales_bot";

export type AdmissionsDefaultLanguage = "en" | "ur" | "roman_ur";

export type SecondaryBotRule = {
  botType: BotType;
  keywords?: string[];
  enabled?: boolean;
};

export type SalesConfigShape = {
  shopName?: string;
  shopAddress?: string;
  shopMapUrl?: string;
  shopTimings?: string;
  supportPhone?: string;
  welcomeMessage?: string;
};

export type BotConfigShape = {
  botType?: BotType;
  secondaryBots?: SecondaryBotRule[];
  bookingConfig?: Record<string, unknown>;
  admissionsConfig?: Record<string, unknown>;
  salesConfig?: SalesConfigShape;
};

export const BOT_TYPE_OPTIONS: { value: BotType; label: string }[] = [
  { value: "booking_bot", label: "Booking Bot" },
  { value: "admissions_bot", label: "Admissions Bot" },
  { value: "sales_bot", label: "Sales Bot" },
];

export const DEFAULT_ADMISSIONS_CONFIG = {
  universityName: "Greenwich University",
  supportPhone: "",
  supportEmail: "",
  campusAddress: "",
  defaultLanguage: "en" as AdmissionsDefaultLanguage,
};

export const DEFAULT_SALES_CONFIG: SalesConfigShape = {
  shopName: "JV Mobiles",
  shopAddress: "",
  shopMapUrl: "",
  shopTimings: "",
  supportPhone: "",
  welcomeMessage: "",
};

export function getBotTypeLabel(botType?: string): string {
  const match = BOT_TYPE_OPTIONS.find((o) => o.value === botType);
  return match?.label || botType || "-";
}

export function isAdmissionsBot(botType?: string): boolean {
  return botType === "admissions_bot";
}

export function isBookingBot(botType?: string): boolean {
  return botType === "booking_bot";
}

export function isSalesBot(botType?: string): boolean {
  return botType === "sales_bot";
}

/** @deprecated Use getSecondaryBotTypes for multi-bot support */
export function getOtherBotType(botType: BotType): BotType {
  const others = getSecondaryBotTypes(botType);
  return others[0] || "booking_bot";
}

export function getSecondaryBotTypes(defaultBotType: BotType): BotType[] {
  return BOT_TYPE_OPTIONS.map((o) => o.value).filter(
    (type) => type !== defaultBotType,
  );
}

export function isBotEnabled(
  config: BotConfigShape | undefined,
  botType: BotType,
): boolean {
  if (!config?.botType) return false;
  if (config.botType === botType) return true;
  return (config.secondaryBots || []).some(
    (rule) => rule.enabled !== false && rule.botType === botType,
  );
}

export function configHasSalesBot(config: {
  numberType?: string;
  status?: { isActive?: boolean };
  botConfig?: BotConfigShape;
}): boolean {
  return (
    config.numberType === "ai_agent" &&
    !!config.status?.isActive &&
    isBotEnabled(config.botConfig, "sales_bot")
  );
}

export function getEnabledBotTypes(
  config: BotConfigShape | undefined,
): BotType[] {
  if (!config?.botType) return [];
  const types: BotType[] = [config.botType];
  for (const rule of config.secondaryBots || []) {
    if (rule.enabled === false || !rule.botType) continue;
    if (!types.includes(rule.botType)) {
      types.push(rule.botType);
    }
  }
  return types;
}

export function getSecondaryRule(
  config: BotConfigShape | undefined,
  botType: BotType,
): SecondaryBotRule | undefined {
  return (config?.secondaryBots || []).find((rule) => rule.botType === botType);
}

export function keywordsToString(keywords?: string[]): string {
  return (keywords || []).join(", ");
}

export function parseKeywordsInput(value: string): string[] {
  return value
    .split(",")
    .map((kw) => kw.trim().toLowerCase())
    .filter(Boolean);
}

export function formatBotConfigSummary(
  config: BotConfigShape | undefined,
): string {
  if (!config?.botType) return "-";
  const parts = [`${getBotTypeLabel(config.botType)} (default)`];
  for (const rule of config.secondaryBots || []) {
    if (rule.enabled === false) continue;
    parts.push(`${getBotTypeLabel(rule.botType)} via keywords`);
  }
  return parts.join(" + ");
}

export function secondaryBotKeywordPlaceholder(botType: BotType): string {
  if (botType === "admissions_bot") {
    return "admission, apply, university";
  }
  if (botType === "sales_bot") {
    return "iphone, buy, price, mobile";
  }
  return "book, booking, court";
}
